import { isObject } from "helpers/system";
import isEmpty from "lodash/isEmpty";
import { handleActions } from "redux-actions";
import { load, store } from "./filtersStorage";

type DefaultActionTypes = {
    GET_ITEMS: string;
    SET_LOADING: string;
    MERGE_PARAMS: string;
    REPLACE_PARAMS: string;
    CLEAR_PARAMS: string;
};
type DefaultReducerBody<T> = {
    items: T[];
    params: {};
    count: number;
    maxCount: number;
    isLoadingList: boolean;
    isLoadingCount: boolean;
    shouldUpdate: boolean;
    itemsLoadedAt: number;
};

const dateFilters = [
    "lastBidPointPlanEnterDate",
    "firstBidPointPlanEnterDate",
    "lastBidPoint/planEnterDate",
    "firstBidPoint/planEnterDate",
    "lastBidPoint/planEnterDateOffset",
    "firstBidPoint/planEnterDateOffset",
    "lastFixedAt",
    "ge",
    "le",
];

const notStorableKeys = ["suitable-distribution-bids"];

export const deletedFilters = () => ({
    preparedFilter: { and: { isDeleted: false } },
    customFilters: { isDeleted: { filterType: "boolean", id: "isDeleted", value: "false" } },
});

export function defaultConstants(prefix = "") {
    return {
        GET_ITEMS: `GET_${prefix}_LIST`,
        SET_LOADING: `SET_${prefix}_LOADING`,
        MERGE_PARAMS: `MERGE_${prefix}_PARAMS`,
        REPLACE_PARAMS: `REPLACE_${prefix}_PARAMS`,
        CLEAR_PARAMS: `CLEAR_${prefix}_PARAMS`,
    };
}

const formatDateObject = (obj: string | { [key: string]: string | { value: string | Date } | Date }) => {
    if (typeof obj === "string" || obj instanceof String) {
        return new Date(obj as string);
    } else {
        Object.keys(obj).forEach((key) => {
            if (typeof obj[key] === "string" && dateFilters.includes(key as string)) {
                obj[key] = new Date(obj[key] as string);
            }
        });
        return obj;
    }
};

const prepareDateValues = (obj: { [key: string]: string | {} }) => {
    (Object.keys(obj) as string[]).forEach((key) => {
        if (dateFilters.includes(key)) {
            obj[key] = formatDateObject(obj[key]);
        }
        if (isObject(obj[key])) {
            prepareDateValues(obj[key] as {});
        }
    });
};

export function defaultReducer<T, S>(
    actionTypes: DefaultActionTypes,
    filterParams: { orderBy?: {}; preparedFilter?: {}; customFilters?: {} } = {},
    prefix = "",
) {
    const { orderBy, preparedFilter, customFilters } = filterParams;
    const storedParams = load(`${prefix}_params`, {});
    prepareDateValues(storedParams.preparedFilter || {});
    prepareDateValues(storedParams.customFilters || {});
    const orderParam = isEmpty(orderBy) ? {} : { orderBy };
    const preparedFilterParam = isEmpty(preparedFilter) ? {} : { preparedFilter };
    const customFiltersParam = isEmpty(customFilters) ? {} : { customFilters };
    const initialState: S = {
        key: prefix,
        items: [],
        params: {
            start: 0,
            length: 30,
            filters: [],
            customFilters: {},
            preparedFilter: {},
            queryFilter: {},
            ...orderParam,
            ...preparedFilterParam,
            ...customFiltersParam,
            ...storedParams,
        },
        count: 0,
        isLoadingList: true,
        isLoadingCount: true,
        shouldUpdate: true,
    } as S;
    return handleActions(defaultReducerBody<T, S>(actionTypes, initialState as S & { params: {} }), initialState);
}

export function defaultReducerBody<T, S>(
    actionTypes: DefaultActionTypes,
    initialState: S & { params: {} },
): { [x: string]: (s: S, payload: any) => S } {
    const { GET_ITEMS, SET_LOADING, MERGE_PARAMS, REPLACE_PARAMS, CLEAR_PARAMS } = actionTypes;
    return {
        [GET_ITEMS]: (
            state: { params: {} },
            { payload }: { payload: { data: Array<T>; count: number; maxCount: number; start: number } },
        ) => ({
            ...state,
            items: payload.data,
            count: payload.count,
            isLoadingList: false,
            isLoadingCount: false,
            shouldUpdate: false,
            maxCount: payload.maxCount,
            itemsLoadedAt: Date.now(),
            ...(payload.start !== undefined ? { params: { ...state.params, start: payload.start } } : {}),
        }),
        [SET_LOADING]: (state: {}, { payload = true }: { payload: {} }) => ({
            ...state,
            isLoadingList: payload,
            isLoadingCount: payload,
            shouldUpdate: false,
        }),
        [MERGE_PARAMS]: (state: { params: {}; key: string }, { payload }: { payload: {} }) => {
            const params = { ...state.params, ...payload };
            if (!notStorableKeys.includes(state.key)) {
                store(`${state.key}_params`, params);
            }
            return {
                ...state,
                maxCount: undefined,
                params,
                shouldUpdate: true,
            };
        },
        [REPLACE_PARAMS]: (state: {}, { payload }: { payload: {} }) => ({
            ...state,
            params: { ...initialState.params, ...payload },
            shouldUpdate: true,
        }),
        [CLEAR_PARAMS]: (state: {}, { payload }: { payload: {} }) => ({
            ...state,
            params: { ...initialState.params, ...payload },
        }),
    } as unknown as { [x: string]: (s: S, payload: any) => S };
}

export { handleActions };
export type { DefaultActionTypes, DefaultReducerBody };
